import { useCallback, useSyncExternalStore } from 'react';
import type { ObjectData, SceneStore } from '@os/doc';

/** One object's immutable snapshot; re-renders only when this object changes. Undefined once deleted or invalid. */
export function useObject(store: SceneStore, id: string): Readonly<ObjectData> | undefined {
  const subscribe = useCallback((cb: () => void) => store.subscribe(id, cb), [store, id]);
  return useSyncExternalStore(subscribe, () => store.getObject(id));
}

const EMPTY: readonly string[] = [];
const childCache = new WeakMap<SceneStore, Map<string | null, readonly string[]>>();

function childIds(store: SceneStore, parentId: string | null): readonly string[] {
  const next = store.graph().children.get(parentId) ?? EMPTY;
  let cache = childCache.get(store);
  if (!cache) childCache.set(store, (cache = new Map()));
  const prev = cache.get(parentId);
  if (prev && prev.length === next.length && prev.every((id, i) => id === next[i])) return prev;
  cache.set(parentId, next);
  return next;
}

/** Direct children of `parentId` (the board's roots for null) in z order; the array only changes when they do. */
export function useChildIds(store: SceneStore, parentId: string | null): readonly string[] {
  const subscribe = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  return useSyncExternalStore(subscribe, () => childIds(store, parentId));
}

const idsCache = new WeakMap<SceneStore, { key: string; ids: readonly string[] }>();

function objectIds(store: SceneStore): readonly string[] {
  const ids = store.list().map((o) => o.id);
  const key = ids.join(',');
  const cached = idsCache.get(store);
  if (cached?.key === key) return cached.ids;
  idsCache.set(store, { key, ids });
  return ids;
}

/** Object ids in z order. The array only changes on add, delete or reorder, never on a plain edit. */
export function useObjectIds(store: SceneStore): readonly string[] {
  const subscribe = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  return useSyncExternalStore(subscribe, () => objectIds(store));
}
